import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Subject } from 'rxjs';
import { ApplicationDefinition } from '../models/application-definition/application-definition';
import { AttributeDefinition, isSnapshotEligible } from '../models/attributes/attributeDefinition';
import { map } from 'rxjs/operators';
import { ApplicationDefinitionSummary } from '../models/application-definition/application-definition-summary';
import { ApplicationDefinitionEdit } from '../models/application-definition/application-definition-edit';
import { BaseService } from './base.service';
import { ApplicationDefinitionDeleteResponse } from '../models/application-definition/application-definition-delete-response';
import { ApplicationDefinitionClone } from '../models/application-definition/application-definition-clone';
import { FormSummary } from '../models/application-definition/form-summary';
import { FormType } from 'src/app/shared/models/application/formType';
import { Observable } from 'rxjs/internal/Observable';
import { ApiVersions } from './api-version';
import { FormPage } from '../models/application/formPage';
import { ApplicationPayments } from '../models/application-definition/application-payments';
import { AutomationConditionModel } from '../components/automation-condition-builder/automation-condition.model';
import { BubbleDirection } from '../models/bubbleDirection';
import { FormPositionDefinition } from '../models/application-definition/form-position-definition';

@Injectable({
  providedIn: 'root'
})
export class ApplicationDefinitionService extends BaseService {
  private basePath = 'application-definitions';
  public allApplicationDefinitions = new Subject<ApplicationDefinitionSummary[]>();
  public applicationDefinitions = new Subject<ApplicationDefinitionSummary[]>();
  public applicationDefinitionsAvailableToSend = new Subject<ApplicationDefinitionSummary[]>();
  public teamFormsApplicationDefinitions = new Subject<ApplicationDefinitionSummary[]>();
  public sendableTeamFormsApplicationDefinitions = new Subject<ApplicationDefinitionSummary[]>();
  public firmFormsApplicationDefinitions = new Subject<ApplicationDefinitionSummary[]>();
  public applicationDefinition = new Subject<ApplicationDefinition>();
  public applicationDefinitionAttributes = new BehaviorSubject<AttributeDefinition[]>(null);
  public applicationDefinitionDeleted = new Subject<void>();
  private _selectedApplication: ApplicationDefinition;
  private _isSaving = false;

  public get isSaving() {
    return this._isSaving;
  }

  constructor() {
    super();
  }

  getAll() {
    this.get<ApplicationDefinitionSummary[]>(this.basePath)
      .pipe(
        map((data) => {
          if (data) {
            return data.map((app) => {
              app.requestUri = !app.isPrivate
                ? `${window.location.origin}/members/application/${app.id}/request`
                : null;
              return ApplicationDefinitionSummary.parse(app);
            });
          }
        })
      )
      .subscribe((data) => {
        if (data) {
          this.allApplicationDefinitions.next(data);
          this.applicationDefinitions.next(
            data.filter((x) => x.formType == 'Application' && !x.createTeam)
          );
          this.teamFormsApplicationDefinitions.next(data.filter((x) => x.formType == 'Team'));
          this.firmFormsApplicationDefinitions.next(
            data.filter((x) => x.formType == 'Application' && x.createTeam)
          );
        }
      });
  }

  getApplicationsAvailableToSend() {
    this.get<ApplicationDefinitionSummary[]>(`${this.basePath}/available`)
      .pipe(
        map((data) => {
          if (data) {
            return data.map((app) => {
              app.requestUri = !app.isPrivate
                ? `${window.location.origin}/members/application/${app.id}/request`
                : null;
              return ApplicationDefinitionSummary.parse(app);
            });
          }
        })
      )
      .subscribe((data) => {
        if (data) {
          this.applicationDefinitionsAvailableToSend.next(
            data.filter((x) => x.formType == 'Application' && !x.isArchived && !x.hasExpired)
          );
        }
      });
  }

  getOne(applicationId: string): Promise<ApplicationDefinition> {
    return lastValueFrom(
      this.get<ApplicationDefinition>(`${this.basePath}/${applicationId}`, false)
    )
      .then((app) => {
        app.requestUri = `${window.location.origin}/members/application/${app.id}/request`;
        return ApplicationDefinition.parse(app);
      })
      .then((data) => {
        this._selectedApplication = data;
        this.applicationDefinition.next(this._selectedApplication);
        // only snapshot-eligible questions can ever have response data, so
        // only they are offered as report columns
        this.applicationDefinitionAttributes.next(
          this._selectedApplication.questions
            .map((x) => x.attributeDefinition)
            .filter((definition) => isSnapshotEligible(definition))
        );
        return data;
      });
  }

  deleteApplication() {
    return this.delete(`${this.basePath}/${this._selectedApplication.id}`).subscribe(
      (data: ApplicationDefinitionDeleteResponse) => {
        if (data.success) {
          this.openSnackBar(`Successfully deleted ${this._selectedApplication.title}`, true);
          this._selectedApplication = null;
          this.applicationDefinitionDeleted.next();
        } else {
          this.openSnackBar(`${data.errorMessage}`, false);
        }
      }
    );
  }

  archiveApplication(id: string): Observable<any> {
    this._isSaving = true;
    let path = `${this.basePath}/${id}/archive`;
    var obs = this.patch(path).pipe(
      map((data) => {
        if (data) {
          if (this._selectedApplication) {
            return this._selectedApplication.update(data);
          } else {
            return data;
          }
        }
      })
    );
    obs.subscribe((data) => {
      if (data) {
        this.openSnackBar(`Successfully archived ${data.title}`, true);
        this._isSaving = false;
        this.applicationDefinition.next(data);
      }
    });

    return obs;
  }

  unarchiveApplication(id: string): Observable<any> {
    this._isSaving = true;
    let path = `${this.basePath}/${id}/unarchive`;
    var obs = this.patch(path).pipe(
      map((data) => {
        if (data) {
          if (this._selectedApplication) {
            return this._selectedApplication.update(data);
          } else {
            return data;
          }
        }
      })
    );
    obs.subscribe((data) => {
      if (data) {
        this.openSnackBar(`Successfully unarchived ${data.title}`, true);
        this._isSaving = false;
        this.applicationDefinition.next(data);
      }
    });

    return obs;
  }

  updatePaymentSettings = (payments: ApplicationPayments): Promise<ApplicationPayments> => {
    this._isSaving = true;
    return lastValueFrom(
      this.put(
        `${this.basePath}/${this._selectedApplication.id}/paymentSettings`,
        JSON.stringify(payments)
      )
    ).then((result) => {
      this._isSaving = false;
      return result;
    });
  };

  getPaymentSettings = (): Promise<ApplicationPayments> => {
    this._isSaving = true;
    return lastValueFrom(
      this.get(`${this.basePath}/${this._selectedApplication.id}/paymentSettings`)
    ).then((result) => {
      this._isSaving = false;
      return result;
    });
  };

  updatePaymentSettingsAutomation = (
    feeDefinitionId: string,
    model: AutomationConditionModel
  ): Promise<AutomationConditionModel> => {
    this._isSaving = true;
    return lastValueFrom(
      this.put(
        `${this.basePath}/${this._selectedApplication.id}/paymentSettings/automation/${feeDefinitionId}`,
        JSON.stringify(model),
        null,
        ApiVersions.v0_1,
        true
      )
    ).then((result) => {
      this._isSaving = false;
      return result;
    });
  };

  getPaymentSettingsAutomation = (feeDefinitionId: string): Promise<AutomationConditionModel> => {
    this._isSaving = true;
    return lastValueFrom(
      this.get(
        `${this.basePath}/${this._selectedApplication.id}/paymentSettings/automation/${feeDefinitionId}`,
        null,
        ApiVersions.v0_1,
        true
      )
    ).then((result) => {
      this._isSaving = false;
      return result;
    });
  };

  deletePaymentSettingsAutomation = (
    feeDefinitionId: string
  ): Promise<AutomationConditionModel> => {
    this._isSaving = true;
    return lastValueFrom(
      this.delete(
        `${this.basePath}/${this._selectedApplication.id}/paymentSettings/automation/${feeDefinitionId}`,
        null,
        ApiVersions.v0_1,
        true
      )
    ).then((result) => {
      this._isSaving = false;
      return result;
    });
  };

  update(application: ApplicationDefinitionEdit) {
    this._isSaving = true;
    let body = JSON.stringify(application);
    this.put(`${this.basePath}/${this._selectedApplication.id}`, body)
      .pipe(
        map((app) => {
          if (app) {
            app.requestUri = `${window.location.origin}/members/application/${app.id}/request`;
            return this._selectedApplication.update(app);
          }
        })
      )
      .subscribe((data) => {
        this._isSaving = false;
        this.applicationDefinition.next(data);
      });
  }

  create(name: string, formType: FormType, createTeam: boolean = false) {
    let body = JSON.stringify({ title: name, formType: formType, createTeam: createTeam });
    return this.post(`${this.basePath}`, body).pipe(map((data) => data?.id));
  }

  clone(fromId: string, clone: ApplicationDefinitionClone) {
    const body = JSON.stringify(clone);
    return this.post(`${this.basePath}/${fromId}/clone`, body).pipe(map((data) => data?.id));
  }

  getFormsSummaryReport(): Observable<FormSummary[]> {
    return this.get(this.basePath.concat('/forms-summary'), true, ApiVersions.v0_1);
  }

  deletePage(formId: string, formPageId: string) {
    this._isSaving = true;
    return this.delete(`${this.basePath}/${formId}/form-page/${formPageId}`)
      .pipe(
        map((app) => {
          if (app) {
            app.requestUri = `${window.location.origin}/members/application/${app.id}/request`;
            return this._selectedApplication.update(app);
          }
        })
      )
      .subscribe((data) => {
        if (data) {
          this.openSnackBar(
            `Successfully deleted page in ${this._selectedApplication.title}`,
            true
          );
          this._isSaving = false;
          this.applicationDefinition.next(data);
        }
      });
  }

  addFormPage(formPage: FormPage) {
    this._isSaving = true;
    let body = JSON.stringify(formPage);
    this.post(`${this.basePath}/${formPage.formId}/form-page`, body)
      .pipe(
        map((app) => {
          if (app) {
            app.requestUri = `${window.location.origin}/members/application/${app.id}/request`;
            return this._selectedApplication.update(app);
          }
        })
      )
      .subscribe((data) => {
        if (formPage.id) {
          this.openSnackBar(`Successfully updated page, ${formPage.name}`, true);
        } else {
          this.openSnackBar(`Successfully added page, ${formPage.name}`, true);
        }
        this._isSaving = false;
        this.applicationDefinition.next(data);
      });
  }

  updateFormPage(formPage: FormPage) {
    this._isSaving = true;
    let body = JSON.stringify(formPage);
    this.put(`${this.basePath}/${formPage.formId}/form-page/${formPage.id}`, body)
      .pipe(
        map((app) => {
          if (app) {
            app.requestUri = `${window.location.origin}/members/application/${app.id}/request`;
            return this._selectedApplication.update(app);
          }
        })
      )
      .subscribe((data) => {
        if (formPage.id) {
          this.openSnackBar(`Successfully updated page, ${formPage.name}`, true);
        } else {
          this.openSnackBar(`Successfully added page, ${formPage.name}`, true);
        }
        this._isSaving = false;
        this.applicationDefinition.next(data);
      });
  }

  bubbleFormPage(formPage: FormPage, direction: BubbleDirection): Promise<ApplicationDefinition> {
    let body = JSON.stringify(formPage);

    return lastValueFrom(
      this.put(
        `${this.basePath}/${formPage.formId}/form-page/${formPage.id}/bubble/${direction.toString()}`,
        body
      )
    ).then((json) => ApplicationDefinition.parse(json));
  }

  getPositionDefinitions(): Promise<FormPositionDefinition[]> {
    this._isSaving = true;
    return lastValueFrom(
      this.get(
        `${this.basePath}/${this._selectedApplication.id}/position-definitions`,
        null,
        ApiVersions.v0_1
      )
    )
      .then((result: any[]) =>
        (result ?? []).map((positionDefinition) => FormPositionDefinition.parse(positionDefinition))
      )
      .finally(() => {
        this._isSaving = false;
      });
  }

  getPositionDefinition(positionDefinitionId: string): Promise<FormPositionDefinition> {
    this._isSaving = true;
    return lastValueFrom(
      this.get(
        `${this.basePath}/${this._selectedApplication.id}/position-definitions/${positionDefinitionId}`,
        null,
        ApiVersions.v0_1
      )
    )
      .then((result) => FormPositionDefinition.parse(result))
      .finally(() => {
        this._isSaving = false;
      });
  }

  addPositionDefinition(model: Partial<FormPositionDefinition>): Promise<FormPositionDefinition> {
    this._isSaving = true;
    return lastValueFrom(
      this.post(
        `${this.basePath}/${this._selectedApplication.id}/position-definitions`,
        JSON.stringify(model),
        null,
        ApiVersions.v0_1
      )
    )
      .then((result) => FormPositionDefinition.parse(result))
      .finally(() => {
        this._isSaving = false;
      });
  }

  updatePositionDefinition(
    positionDefinitionId: string,
    model: Partial<FormPositionDefinition>
  ): Promise<FormPositionDefinition> {
    this._isSaving = true;
    return lastValueFrom(
      this.put(
        `${this.basePath}/${this._selectedApplication.id}/position-definitions/${positionDefinitionId}`,
        JSON.stringify(model),
        null,
        ApiVersions.v0_1
      )
    )
      .then((result) => FormPositionDefinition.parse(result))
      .finally(() => {
        this._isSaving = false;
      });
  }

  deletePositionDefinition(positionDefinitionId: string): Promise<void> {
    this._isSaving = true;
    return lastValueFrom(
      this.delete(
        `${this.basePath}/${this._selectedApplication.id}/position-definitions/${positionDefinitionId}`,
        null,
        ApiVersions.v0_1
      )
    )
      .then(() => undefined)
      .finally(() => {
        this._isSaving = false;
      });
  }
}
